import React from 'react';

import { css } from '@emotion/css';
import { ModifiedUpdatedMinMaxListDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTaskSummary';
import { Typography } from 'antd';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';

interface DailyWorkflowProjectedInventoryCardProps {
  showAsSingleItem?: boolean;
  minMaxUpdates?: ModifiedUpdatedMinMaxListDTO;
}

export function DailyWorkflowProjectedInventoryCard({
  showAsSingleItem = false,
  minMaxUpdates,
}: DailyWorkflowProjectedInventoryCardProps) {
  const projectedInventoryGainItems = (minMaxUpdates?.items || []).filter(
    (item) => item.projectedInventoryChange && item.projectedInventoryChange > 0,
  );
  const projectedInventoryLossItems = (minMaxUpdates?.items || []).filter(
    (item) => item.projectedInventoryChange && item.projectedInventoryChange < 0,
  );

  const projectedInventoryGain = projectedInventoryGainItems.reduce(
    (total, item) => total + item.projectedInventoryChange,
    0,
  );

  const projectedInventoryLoss = projectedInventoryLossItems.reduce(
    (total, item) => total + item.projectedInventoryChange,
    0,
  );

  const projectedInventoryChange = projectedInventoryGain + projectedInventoryLoss;

  return (
    <div
      className={css`
        margin-bottom: 18px;
        border: 1px solid ${theme.colors.neutral[300]};
        border-radius: 6px;
        padding: 12px 24px;
        overflow: hidden;
      `}
    >
      <FlexSpace direction="row" gap={8} alignItems="center" justify="flex-start">
        <InventoryBoxIcon />
        <Typography.Title level={3} style={{ margin: 0, fontSize: 14, fontWeight: 700, marginBottom: 0 }}>
          Projected Inventory Change Summary
        </Typography.Title>
      </FlexSpace>
      <FlexSpace
        direction="row"
        gap={8}
        alignItems="center"
        className={css`
          margin-top: 10px;
        `}
      >
        {showAsSingleItem ? (
          <ProjectedInventoryChange
            projectedInventoryChange={projectedInventoryChange}
            description="Projected inventory change for updated settings"
          />
        ) : (
          <>
            <ProjectedInventoryChange
              projectedInventoryChange={projectedInventoryChange}
              description={`Total inventory value ${projectedInventoryChange < 0 ? 'reduction' : 'increase'}`}
            />
            <ProjectedInventoryChange
              projectedInventoryChange={projectedInventoryGain}
              description={`[${projectedInventoryGainItems.length}] items with inventory increase`}
            />
            <ProjectedInventoryChange
              projectedInventoryChange={projectedInventoryLoss}
              description={`[${projectedInventoryLossItems.length}] items with inventory decrease`}
            />
          </>
        )}
      </FlexSpace>
    </div>
  );
}

export const GraphArrowIcon = ({ isNegative, ...props }: { isNegative: boolean } & React.SVGProps<SVGSVGElement>) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: !isNegative ? 'rotateX(180deg)' : undefined,
      color: isNegative ? theme.colors.success[500] : theme.colors.danger[500],
    }}
    {...props}
  >
    <path d="M10.8569 12.2589H15.4284V7.68747" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M15.4284 12.2589L8.97129 5.80177C8.86447 5.69707 8.72086 5.63842 8.57129 5.63842C8.42172 5.63842 8.27811 5.69707 8.17129 5.80177L5.54272 8.43034C5.4359 8.53504 5.29229 8.59369 5.14272 8.59369C4.99314 8.59369 4.84953 8.53504 4.74272 8.43034L0.571289 4.25891"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProjectedInventoryChange = ({
  projectedInventoryChange,
  description,
}: {
  projectedInventoryChange: number;
  description: string;
}) => {
  const isNegative = projectedInventoryChange < 0;
  const formattedValue = Math.abs(projectedInventoryChange || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return (
    <FlexSpace
      direction="row"
      gap={8}
      alignItems="center"
      className={css`
        flex-grow: 1;
      `}
    >
      <GraphArrowIcon
        isNegative={!!isNegative}
        className={css`
          font-size: 16px;
          margin-right: 8px;
        `}
      />
      <Typography.Title
        level={1}
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 700,
          color: isNegative ? theme.colors.success[500] : theme.colors.danger[500],
        }}
      >
        {isNegative ? '-' : '+'}
        {formattedValue}
      </Typography.Title>
      <Typography.Text style={{ fontSize: 12, color: theme.colors.neutral[600], fontWeight: 400 }}>
        {description}
      </Typography.Text>
    </FlexSpace>
  );
};

export const InventoryBoxIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.5 10.7392L17.6232 6.83441M10.5 10.7392V18.5487M10.5 10.7392L6.93794 8.7872M17.6232 6.83441C17.3761 6.42838 17.0215 6.09177 16.5953 5.85843L14.25 4.5726M17.6232 6.83441C17.8659 7.2329 18 7.69134 18 8.16835V13.31C17.9997 13.7794 17.8696 14.2403 17.6229 14.646C17.3762 15.0517 17.0217 15.3877 16.5953 15.6199L11.9047 18.1915C11.4771 18.4258 10.9928 18.5489 10.5 18.5487M10.5 18.5487C10.0072 18.5489 9.52294 18.4258 9.09529 18.1915L4.40471 15.6199C3.53559 15.1437 3 14.264 3 13.31V8.16835C2.99987 7.69973 3.12957 7.23945 3.37588 6.83441M6.93794 8.7872L3.37588 6.83441M6.93794 8.7872L14.25 4.5726M14.25 4.5726L11.9047 3.28678C11.4772 3.05216 10.9929 2.92871 10.5 2.92871C10.0071 2.92871 9.52284 3.05216 9.09529 3.28678L4.40471 5.85843C3.97814 6.09165 3.62329 6.42827 3.37588 6.83441"
      stroke="black"
      strokeLinejoin="round"
    />
  </svg>
);
